// The strip above a table (direction B's table bar): search on the left, page filters after it, actions
// on the right. While rows are selected, DataTable swaps it for DataTableSelectionBar. Presentational:
// pages keep the search value, its debounce and every filter in their own state.
import { SearchIcon, XIcon } from 'lucide-react';
import type * as React from 'react';

import { Button } from '@/components/lumen/button';
import { Input } from '@/components/lumen/input';
import { useLanguage } from '@/contexts/LanguageContext';
import { formatTemplate } from '@/lib/format-template';
import { cn } from '@/lib/utils';

const barClasses = 'flex min-h-13 flex-wrap items-center gap-2 border-line border-b px-3 py-2.5 sm:px-4';

export interface DataTableToolbarProps {
  /** A TableSearch. */
  search?: React.ReactNode;
  /** After the search: a FilterToggle, quick selects, a DataTableColumnsMenu. */
  children?: React.ReactNode;
  /** Pushed to the right: counts, export buttons. */
  actions?: React.ReactNode;
  className?: string;
}

export function DataTableToolbar({ search, children, actions, className }: DataTableToolbarProps) {
  return (
    <div data-slot="data-table-toolbar" className={cn(barClasses, className)}>
      {search}
      {children}
      {actions && <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export interface TableSearchProps extends Omit<React.ComponentProps<'input'>, 'value' | 'onChange' | 'type'> {
  value: string;
  /** Called on every keystroke with the new text; debounce in the page, as before. */
  onChange: (value: string) => void;
}

/**
 * The search box of a list page; `className` sizes the wrapper. Its accessible name is `aria-label`, else
 * the placeholder, else "Cari".
 */
export function TableSearch({
  value,
  onChange,
  placeholder,
  className,
  'aria-label': ariaLabel,
  ...props
}: TableSearchProps) {
  const { t } = useLanguage();
  return (
    <div data-slot="table-search" className={cn('relative w-full sm:w-72', className)}>
      <SearchIcon
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-fg-muted"
      />
      <Input
        {...props}
        type="search"
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder ?? t('lumen.table.search')}
        className="h-8 pl-8 text-13 md:text-13"
      />
    </div>
  );
}

export interface DataTableSelectionBarProps {
  count: number;
  onClear: () => void;
  /** The bulk actions (small buttons). */
  children?: React.ReactNode;
  className?: string;
}

/** "3 dipilih" + the bulk actions + clear. DataTable renders it in place of `toolbar` (prop `bulkActions`). */
export function DataTableSelectionBar({ count, onClear, children, className }: DataTableSelectionBarProps) {
  const { t } = useLanguage();
  return (
    <div data-slot="data-table-selection-bar" className={cn(barClasses, 'bg-label/8', className)}>
      <strong aria-live="polite" className="mr-1 font-semibold text-13 text-fg">
        {formatTemplate(t('lumen.table.selected'), { n: count })}
      </strong>
      {children}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="ml-auto"
        onClick={onClear}
        aria-label={t('lumen.table.clearSelection')}
      >
        <XIcon aria-hidden />
      </Button>
    </div>
  );
}
